<template>
  <div class="pdf-document">
    <div class="pdf-document__controls">
      <v-btn
        small
        outlined
        :disabled="busy || pageNumber <= 1"
        @click="changePage(-1)"
      >
        Önceki sayfa
      </v-btn>
      <span role="status">{{ busy ? 'PDF yükleniyor…' : `Sayfa ${pageNumber} / ${pageCount}` }}</span>
      <v-btn
        small
        outlined
        :disabled="busy || pageNumber >= pageCount"
        @click="changePage(1)"
      >
        Sonraki sayfa
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="error"
      outlined
    >
      {{ error }}
    </v-alert>
    <div
      ref="viewport"
      class="pdf-document__viewport"
      :aria-busy="busy"
    >
      <canvas
        ref="canvas"
        :aria-label="`Belge, sayfa ${pageNumber}`"
        role="img"
      ></canvas>
    </div>
  </div>
</template>

<script>
const PDF_BASE = '/pdfjs-6.4.299/'

export default {
  props: { url: { type: String, required: true } },
  data: () => ({
    busy: true, error: '', pageNumber: 1, pageCount: 0,
  }),
  async mounted() {
    this.disposed = false
    try {
      // This same-origin module is copied verbatim at build time, outside webpack 4.
      // eslint-disable-next-line import/no-unresolved, import/no-absolute-path, import/extensions
      const pdfjs = await import(/* webpackIgnore: true */ '/pdfjs-6.4.299/pdf.js')
      if (this.disposed) return
      pdfjs.GlobalWorkerOptions.workerSrc = `${PDF_BASE}pdf.worker.js`
      this.loadingTask = pdfjs.getDocument({
        url: this.url,
        isEvalSupported: false,
        cMapUrl: `${PDF_BASE}cmaps/`,
        cMapPacked: true,
        standardFontDataUrl: `${PDF_BASE}standard_fonts/`,
        wasmUrl: `${PDF_BASE}wasm/`,
      })
      this.pdf = await this.loadingTask.promise
      if (this.disposed) return
      this.pageCount = this.pdf.numPages
      await this.renderPage()
    } catch (error) {
      if (!this.disposed) {
        this.error = 'PDF görüntülenemedi. Belgeyi kapatıp tekrar açın.'
        this.busy = false
      }
    }
  },
  beforeDestroy() {
    this.disposed = true
    if (this.renderTask) this.renderTask.cancel()
    if (this.loadingTask) this.loadingTask.destroy()
  },
  methods: {
    async changePage(delta) {
      if (this.busy) return
      this.pageNumber = Math.max(1, Math.min(this.pageCount, this.pageNumber + delta))
      await this.renderPage()
    },
    async renderPage() {
      this.busy = true
      try {
        const page = await this.pdf.getPage(this.pageNumber)
        if (this.disposed) return
        const original = page.getViewport({ scale: 1 })
        const width = Math.max(200, this.$refs.viewport.clientWidth - 24)
        const scale = Math.min(width / original.width, 2)
        const viewport = page.getViewport({ scale })
        const density = Math.min(window.devicePixelRatio || 1, 2)
        const { canvas } = this.$refs
        canvas.width = Math.floor(viewport.width * density)
        canvas.height = Math.floor(viewport.height * density)
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`
        this.renderTask = page.render({
          canvasContext: canvas.getContext('2d'),
          viewport,
          transform: density === 1 ? null : [density, 0, 0, density, 0, 0],
        })
        await this.renderTask.promise
        if (!this.disposed) this.$refs.viewport.scrollTop = 0
      } catch (error) {
        if (!this.disposed) this.error = 'PDF sayfası görüntülenemedi.'
      } finally {
        if (!this.disposed) this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.pdf-document__controls { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.pdf-document__viewport { max-height: 65vh; overflow: auto; background: #e5e7eb; padding: 12px; text-align: center; }
.pdf-document__viewport canvas { display: block; margin: auto; background: white; max-width: 100%; }
</style>
